import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/lib/supabase';
import SeoHead, { SITE_URL } from '@/components/feature/SeoHead';

interface Article {
  id: number;
  title_zh: string;
  summary_zh: string;
  cover_img: string;
  category_id: number;
  tags: string[];
  published_at: string;
  article_categories: { name_zh: string } | null;
}

const catOverlayMap: Record<string, string> = {
  '医学知识': 'bg-[#1a3a5c]/85 text-white',
  '法律政策': 'bg-amber-700/85 text-white',
  '常见问题': 'bg-[#c9a96e]/85 text-white',
  '干货科普': 'bg-green-700/85 text-white',
};

export default function TagsPage() {
  const { t } = useTranslation();
  const { tag } = useParams<{ tag: string }>();
  const navigate = useNavigate();
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tag) return;
    setLoading(true);
    supabase
      .from('articles')
      .select('id, title_zh, summary_zh, cover_img, category_id, tags, published_at, article_categories(name_zh)')
      .eq('status', 'published')
      .lte('published_at', new Date().toISOString())
      .order('published_at', { ascending: false })
      .then(({ data }) => {
        const filtered = (data ?? []).filter((a) => {
          const tags = Array.isArray(a.tags) ? a.tags : (typeof a.tags === 'string' ? JSON.parse(a.tags) : []);
          return tags.includes(tag);
        });
        setArticles(filtered as Article[]);
        setLoading(false);
      });
  }, [tag]);

  return (
    <>
      <SeoHead
        title={`#${tag}相关文章 - RTB吉尔吉斯斯坦试管代孕中心`}
        description={`浏览与「${tag}」相关的代孕、试管婴儿专业文章，RTB吉尔吉斯斯坦试管代孕中心知识库为您深度解读。`}
        keywords={`${tag},代孕知识,试管婴儿,吉尔吉斯斯坦代孕`}
        canonical={`/tags/${tag}`}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: `#${tag} 相关文章 - RTB吉尔吉斯斯坦试管代孕中心`,
          description: `浏览与"${tag}"标签相关的所有专业代孕、试管婴儿知识文章。`,
          url: `${SITE_URL}/tags/${tag}`,
        }}
      />
      <main className="pt-16 md:pt-20 min-h-screen bg-[#f8f5f0]">
        <section className="bg-[#1a2f4a] py-12">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-white/60 text-sm mb-4 cursor-pointer hover:text-white transition-colors">
              <i className="ri-arrow-left-line"></i>{t('back')}
            </button>
            <div className="flex items-center gap-3">
              <span className="text-2xl font-bold text-white">#</span>
              <h1 className="text-2xl md:text-3xl font-bold text-white">{tag}</h1>
              <span className="ml-2 text-sm text-white/50 bg-white/10 px-3 py-1 rounded-full">{articles.length} {t('knowledge_tag_articles')}</span>
            </div>
            <p className="text-white/60 text-sm mt-2">{t('knowledge_tag_related').replace('{{tag}}', tag || '')}</p>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
          {loading ? (
            <div className="text-center py-20 text-gray-400">
              <i className="ri-loader-4-line text-3xl animate-spin block mb-3"></i>
              <p className="text-sm">{t('knowledge_loading')}</p>
            </div>
          ) : articles.length === 0 ? (
            <div className="text-center py-20 text-gray-400">
              <i className="ri-article-line text-4xl mb-3 block"></i>
              <p className="text-sm">{t('knowledge_no_articles')}</p>
              <Link to="/news" className="mt-4 inline-block text-[#c9a96e] text-sm hover:underline cursor-pointer">{t('knowledge_tag_view_all')}</Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {articles.map((art) => {
                const catName = art.article_categories?.name_zh || '';
                const tags = Array.isArray(art.tags) ? art.tags : (typeof art.tags === 'string' ? JSON.parse(art.tags) : []);
                return (
                  <Link
                    to={`/news/${art.id}`}
                    key={art.id}
                    className="group bg-white rounded-2xl overflow-hidden hover:-translate-y-1 transition-all duration-300 cursor-pointer"
                  >
                    <div className="w-full h-48 overflow-hidden relative">
                      <img
                        src={art.cover_img || 'https://readdy.ai/api/search-image?query=medical%20knowledge%20article%20fertility&width=600&height=360&seq=tag-default&orientation=landscape'}
                        alt={art.title_zh}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-3 left-3">
                        <span className={`inline-block text-xs font-medium px-3 py-1 rounded-full backdrop-blur-sm ${catOverlayMap[catName] || 'bg-[#1a3a5c]/80 text-white'}`}>{catName}</span>
                      </div>
                    </div>
                    <div className="p-5">
                      <h3 className="font-bold text-[#1a3a5c] text-sm leading-snug mb-2 line-clamp-2">{art.title_zh}</h3>
                      <p className="text-gray-500 text-xs leading-relaxed line-clamp-2 mb-4">{art.summary_zh}</p>
                      <div className="flex flex-wrap gap-1 mb-3">
                        {tags.slice(0, 3).map((t: string) => (
                          <Link
                            key={t}
                            to={`/tags/${t}`}
                            onClick={(e) => e.stopPropagation()}
                            className={`text-xs px-2 py-0.5 rounded-full cursor-pointer transition-colors whitespace-nowrap ${
                              t === tag ? 'bg-[#1a3a5c] text-white' : 'bg-gray-100 text-gray-500 hover:bg-[#f0e8d8] hover:text-[#c9a96e]'
                            }`}
                          >
                            #{t}
                          </Link>
                        ))}
                      </div>
                      <div className="flex items-center gap-2 text-gray-400 text-xs pt-3 border-t border-gray-100">
                        <span>{art.published_at ? art.published_at.slice(0, 10) : ''}</span>
                        <span className="ml-auto flex items-center gap-0.5 text-[#c9a96e]">
                          {t('knowledge_read_full')} <i className="ri-arrow-right-line text-xs"></i>
                        </span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </>
  );
}